import React from 'react';

const ResolutionOptionsDrawer = ({
  open,
  onClose,
  rowers = [],
  coaches = [],
  getCurrentResolution,
  hasAppliedResolution,
  onChangeResolution,
  onSendReminder,
  sentReminders = new Set(),
}) => {
  if (!open) return null;

  const renderOptions = (userId, date) => {
    const current = getCurrentResolution(userId, date);
    const applied = hasAppliedResolution(userId, date);
    const options = [
      { action: 'ignore', title: 'Ignore from planning' },
      { action: 'assume_available', title: 'Assume fully available' },
    ];
    return (
      <div
        className={`rounded border p-2 ${applied ? 'border-green-200 bg-green-50' : 'border-gray-100 bg-gray-50'}`}
      >
        <div className="mb-2 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <span className="text-xs font-medium text-gray-700">
              {new Date(date).toLocaleDateString('en-GB')}
            </span>
            {applied && (
              <span className="inline-flex items-center rounded bg-green-100 px-1.5 py-0.5 text-xs font-medium text-green-800">
                Applied
              </span>
            )}
          </div>
        </div>
        <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
          {options.map((opt) => (
            <label
              key={opt.action}
              className="flex cursor-pointer items-center space-x-2 rounded p-1 hover:bg-white"
            >
              <input
                type="radio"
                name={`resolution-${userId}-${date}`}
                value={opt.action}
                checked={current === opt.action}
                onChange={() => onChangeResolution(userId, opt.action, date)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-xs text-gray-700">{opt.title}</span>
            </label>
          ))}
        </div>
      </div>
    );
  };

  const Section = ({ title, users }) => (
    <div className="mb-4">
      <h4 className="mb-3 text-sm font-medium text-gray-900">{title}</h4>
      {users.length === 0 ? (
        <div className="rounded border p-3 text-xs text-gray-500">None</div>
      ) : (
        users.map((user) => {
          const hasReminderSent = sentReminders.has(user.user_id);
          const userName = user.userInfo
            ? `${user.userInfo.name} (${user.userInfo.email})`
            : `User ${user.user_id}`;
          return (
            <div key={user.user_id} className="mb-3 rounded border border-gray-200 p-2.5">
              <div className="mb-2 flex items-center justify-between gap-1.5">
                <div className="min-w-0">
                  <h5 className="text-sm font-medium text-gray-900">{userName}</h5>
                  <p className="text-xs text-gray-600">User ID: {user.user_id}</p>
                </div>
                <div className="flex flex-wrap items-center gap-1.5">
                  <button
                    onClick={() => onChangeResolution(user.user_id, 'assume_available', null)}
                    className="rounded bg-green-50 px-2 py-1 text-[11px] text-xs text-green-700 hover:bg-green-100"
                    title="Assume fully available for all listed dates"
                  >
                    Assume all
                  </button>
                  <button
                    onClick={() => onChangeResolution(user.user_id, 'ignore', null)}
                    className="rounded bg-gray-50 px-2 py-1 text-[11px] text-xs text-gray-700 hover:bg-gray-100"
                    title="Ignore from planning for all listed dates"
                  >
                    Ignore all
                  </button>
                  <button
                    onClick={() => onSendReminder(user.user_id)}
                    disabled={hasReminderSent}
                    className={`inline-flex items-center rounded px-2 py-1 text-xs font-medium ${hasReminderSent ? 'cursor-not-allowed bg-gray-100 text-gray-500' : 'bg-blue-100 text-blue-700 hover:bg-blue-200'}`}
                  >
                    {hasReminderSent ? 'Reminder Sent' : 'Send Reminder'}
                  </button>
                </div>
              </div>
              <div className="space-y-1.5">
                <p className="text-xs font-medium text-red-600">
                  Resolutions for {user.dates.length} date(s):
                </p>
                {user.dates.map((date, idx) => (
                  <div key={idx}>{renderOptions(user.user_id, date)}</div>
                ))}
              </div>
            </div>
          );
        })
      )}
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative flex max-h-[90vh] w-full max-w-3xl flex-col bg-white shadow-xl">
        <div className="flex flex-shrink-0 items-center justify-between border-b px-4 py-3">
          <h3 className="text-base font-medium text-gray-900">Resolution Options</h3>
          <button onClick={onClose} className="text-sm text-gray-500 hover:text-gray-700">
            Close
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-4 py-3">
          {/* Global Actions for All Users */}
          {(rowers.length > 0 || coaches.length > 0) && (
            <div className="mb-6 rounded-lg border border-gray-200 bg-gray-50 p-3">
              <h4 className="mb-3 text-sm font-medium text-gray-900">Global Actions</h4>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => {
                    // Apply "assume_available" to all users for all their dates
                    const allUsers = [...rowers, ...coaches];
                    allUsers.forEach((user) => {
                      user.dates.forEach((date) => {
                        onChangeResolution(user.user_id, 'assume_available', date);
                      });
                    });
                  }}
                  className="rounded bg-green-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-green-700"
                  title="Assume all users are fully available for all their missing dates"
                >
                  Assume All Available
                </button>
                <button
                  onClick={() => {
                    // Apply "ignore" to all users for all their dates
                    const allUsers = [...rowers, ...coaches];
                    allUsers.forEach((user) => {
                      user.dates.forEach((date) => {
                        onChangeResolution(user.user_id, 'ignore', date);
                      });
                    });
                  }}
                  className="rounded bg-gray-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-gray-700"
                  title="Ignore all users from planning for all their missing dates"
                >
                  Ignore All Users
                </button>
              </div>
            </div>
          )}

          <Section title="Rowers" users={rowers} />
          <Section title="Coaches" users={coaches} />
        </div>
      </div>
    </div>
  );
};

export default ResolutionOptionsDrawer;
